/**
 * seed command — create initial admin user via Better Auth
 *
 * Generates a seed script at admin/scripts/seed.ts and runs it with tsx.
 * The script uses Better Auth's server API to create the user, then
 * sets the role to 'admin' via a direct database update.
 */

import fs from 'node:fs'
import path from 'node:path'
import * as clack from '@clack/prompts'
import type { SeedCommandOptions } from '@core-engine/commands/runtime.js'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import { spinner } from '@core-engine/utils/spinner.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace.js'
import { resolveConfig } from '@next-adapter/config.js'

export function buildSeedScript(authBasePath = '/api/admin/auth'): string {
  // Self-contained: inlines DB + auth setup so tsx doesn't need generated aliases.
  // Only relative import is ../lib/db/schema which uses npm packages only.
  return `/**
 * BetterStart Admin — Seed Script
 * Creates the initial admin user
 * AUTO-GENERATED — safe to delete after running
 */

import { readFileSync } from 'node:fs'
import { resolve } from 'node:path'

// Load .env.local (inline — avoids @next/env dependency)
for (const envFile of ['.env.local', '.env']) {
  try {
    const content = readFileSync(resolve(process.cwd(), envFile), 'utf-8')
    for (const line of content.split('\\n')) {
      const trimmed = line.trim()
      if (!trimmed || trimmed.startsWith('#')) continue
      const eqIdx = trimmed.indexOf('=')
      if (eqIdx === -1) continue
      const key = trimmed.slice(0, eqIdx).trim()
      const val = trimmed.slice(eqIdx + 1).trim().replace(/^['"]|['"]$/g, '')
      if (!process.env[key]) process.env[key] = val
    }
  } catch {}
}

import { drizzle } from 'drizzle-orm/postgres-js'
import { eq } from 'drizzle-orm'
import postgres from 'postgres'
import * as schema from '../lib/db/schema'
import { betterAuth } from 'better-auth'
import { drizzleAdapter } from 'better-auth/adapters/drizzle'

// Inline DB connection (mirrors generated lib/db/client.ts)
const databaseUrl = process.env.DATABASE_URL
if (!databaseUrl) {
  throw new Error('DATABASE_URL is required')
}

const client = postgres(databaseUrl)
const db = drizzle({ client, schema })

// Inline auth setup (mirrors generated lib/actions/auth/server.ts)
const auth = betterAuth({
  secret: process.env.BETTERSTART_AUTH_SECRET,
  baseURL: process.env.BETTERSTART_AUTH_URL,
  basePath: process.env.BETTERSTART_AUTH_BASE_PATH || '${authBasePath}',
  database: drizzleAdapter(db, {
    provider: 'pg',
    schema: {
      user: schema.user,
      session: schema.session,
      account: schema.account,
      verification: schema.verification,
    },
  }),
  emailAndPassword: { enabled: true, minPasswordLength: 8 },
  user: {
    additionalFields: {
      role: { type: 'string', required: false, defaultValue: 'member', input: false },
    },
  },
})

const CHECK_ADMIN_ONLY = process.env.SEED_CHECK_ADMIN === 'true'
const OVERWRITE_MODE = process.env.SEED_OVERWRITE_MODE

async function clearAuthorshipReferences(userId: string) {
  for (const table of Object.values(schema)) {
    if (!table || typeof table !== 'object') continue
    const tableRef = table as unknown as Record<string, unknown>

    if (tableRef.createdBy) {
      await db
        .update(table as never)
        .set({ createdBy: null })
        .where(eq(tableRef.createdBy as never, userId))
    }

    if (tableRef.updatedBy) {
      await db
        .update(table as never)
        .set({ updatedBy: null })
        .where(eq(tableRef.updatedBy as never, userId))
    }
  }
}

async function main() {
  const existingAdmin = await db
    .select({ id: schema.user.id, email: schema.user.email, name: schema.user.name })
    .from(schema.user)
    .where(eq(schema.user.role, 'admin'))
    .then((rows: { id: string; email: string; name: string }[]) => rows[0])

  if (CHECK_ADMIN_ONLY) {
    if (existingAdmin) {
      console.log(\`EXISTING_ADMIN:\${JSON.stringify(existingAdmin)}\`)
      process.exit(3)
    }
    process.exit(0)
  }

  const EMAIL = process.env.SEED_EMAIL
  const PASSWORD = process.env.SEED_PASSWORD
  const NAME = process.env.SEED_NAME || 'Admin'

  if (!EMAIL || !PASSWORD) {
    console.error('  SEED_EMAIL and SEED_PASSWORD are required.')
    process.exit(1)
  }

  // Check if a user already exists with the requested email
  const existingUser = await db
    .select({ id: schema.user.id, name: schema.user.name })
    .from(schema.user)
    .where(eq(schema.user.email, EMAIL))
    .then((rows: { id: string; name: string }[]) => rows[0])

  const replaceTarget =
    OVERWRITE_MODE === 'admin' ? existingAdmin : OVERWRITE_MODE === 'email' ? existingUser : undefined

  if (existingUser && !replaceTarget) {
    // Exit code 2 signals "user exists" to the CLI
    console.log(\`EXISTING_USER:\${existingUser.name}\`)
    process.exit(2)
  }

  if (replaceTarget) {
    console.log(
      OVERWRITE_MODE === 'admin'
        ? '\\n  Replacing existing admin user...'
        : '\\n  Replacing existing account...'
    )
    await clearAuthorshipReferences(replaceTarget.id)
    // Remove existing account + session rows first (foreign key refs)
    await db.delete(schema.session).where(eq(schema.session.userId, replaceTarget.id))
    await db.delete(schema.account).where(eq(schema.account.userId, replaceTarget.id))
    await db.delete(schema.user).where(eq(schema.user.id, replaceTarget.id))
  } else {
    console.log('\\n  Creating admin user...')
  }

  console.log(\`  Email: \${EMAIL}\\n\`)

  const result = await auth.api.signUpEmail({
    body: { email: EMAIL, password: PASSWORD, name: NAME },
  })

  if (!result?.user) {
    console.error('  Failed to create user.')
    process.exit(1)
  }

  await db
    .update(schema.user)
    .set({ role: 'admin' })
    .where(eq(schema.user.id, result.user.id))

  console.log(\`  Admin user \${replaceTarget ? 'replaced' : 'created'}: \${EMAIL}\`)
  console.log('  Role: admin\\n')
  process.exit(0)
}

main().catch((err) => {
  console.error('  Seed failed:', err.message || err)
  process.exit(1)
})
`
}

export async function runSeedCommand(options: SeedCommandOptions): Promise<void> {
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()

  clack.intro('BetterStart Seed')

  // Load config
  let config: ResolvedBetterstartConfig
  try {
    config = await resolveConfig(cwd)
  } catch (err) {
    clack.cancel(`Error loading config: ${err instanceof Error ? err.message : String(err)}`)
    process.exit(1)
  }

  const adminDir = config.paths?.admin ?? './admin'

  // Prompt for user details
  const email = await clack.text({
    message: 'Admin email',
    placeholder: 'admin@example.com',
    validate: (v) => {
      if (!v || !v.includes('@')) return 'Please enter a valid email'
    }
  })
  if (clack.isCancel(email)) {
    clack.cancel('Cancelled.')
    return
  }

  const password = await clack.password({
    message: 'Admin password',
    validate: (v) => {
      if (!v || v.length < 8) return 'Password must be at least 8 characters'
    }
  })
  if (clack.isCancel(password)) {
    clack.cancel('Cancelled.')
    return
  }

  const name = await clack.text({
    message: 'Admin name',
    placeholder: 'Admin',
    defaultValue: 'Admin'
  })
  if (clack.isCancel(name)) {
    clack.cancel('Cancelled.')
    return
  }

  // Write seed script
  const scriptsDir = path.join(cwd, adminDir, 'scripts')
  const seedPath = path.join(scriptsDir, 'seed.ts')

  if (!fs.existsSync(scriptsDir)) {
    fs.mkdirSync(scriptsDir, { recursive: true })
  }
  const namespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  fs.writeFileSync(seedPath, buildSeedScript(`${namespace.apiPath}/auth`), 'utf-8')

  const { execFile } = await import('node:child_process')
  const tsxBin = path.join(cwd, 'node_modules', '.bin', 'tsx')

  const runSeed = (overwrite: boolean) =>
    new Promise<{ code: number; stdout: string }>((resolve, reject) => {
      execFile(
        tsxBin,
        [seedPath],
        {
          cwd,
          env: {
            ...process.env,
            SEED_EMAIL: email,
            SEED_PASSWORD: password,
            SEED_NAME: name || 'Admin',
            ...(overwrite ? { SEED_OVERWRITE_MODE: 'email' } : {})
          }
        },
        (err, stdout, stderr) => {
          if (err && 'code' in err && err.code === 2) {
            // Exit code 2 = user already exists
            resolve({ code: 2, stdout })
          } else if (err) {
            reject(new Error(stderr || err.message))
          } else {
            resolve({ code: 0, stdout })
          }
        }
      )
    })

  const seedSpinner = spinner()
  seedSpinner.start('Creating admin user...')

  try {
    const result = await runSeed(false)

    if (result.code === 2) {
      // User already exists — ask to overwrite
      const existingName =
        result.stdout
          .split('\n')
          .find((l) => l.startsWith('EXISTING_USER:'))
          ?.replace('EXISTING_USER:', '')
          ?.trim() || 'unknown'

      seedSpinner.stop(`Account already exists for ${email}`)

      const overwrite = await clack.confirm({
        message: `An admin account (${existingName}) already exists with this email. Replace it?`
      })
      if (clack.isCancel(overwrite) || !overwrite) {
        clack.cancel('Seed cancelled.')
        // Clean up before exiting
        try {
          fs.unlinkSync(seedPath)
        } catch {}
        return
      }

      seedSpinner.start('Replacing admin user...')
      await runSeed(true)
      seedSpinner.stop('Admin user replaced')
    } else {
      seedSpinner.stop('Admin user created')
    }
  } catch (err) {
    seedSpinner.stop('Failed to create admin user')

    const errMsg = err instanceof Error ? err.message : String(err)
    clack.log.error(errMsg)
    clack.log.info('You can run the seed script manually:')
    clack.log.info(
      `  SEED_EMAIL="${email}" SEED_PASSWORD="..." npx tsx ${path.relative(cwd, seedPath)}`
    )
    clack.outro('')
    process.exit(1)
  }

  // Clean up seed script
  try {
    fs.unlinkSync(seedPath)
    // Remove scripts dir if empty
    if (fs.existsSync(scriptsDir) && fs.readdirSync(scriptsDir).length === 0) {
      fs.rmdirSync(scriptsDir)
    }
  } catch {
    // Not critical
  }

  clack.outro(`Admin user ready: ${email}`)
}
